import { ZodError } from 'zod';
import { ApiError } from './ApiError';
import { NavigableError } from './NavigableError';
import { RetryableError } from './RetryableError';

const FALLBACK = 'An unexpected error occurred. Please try again.';
const NETWORK_ERROR =
  'Unable to connect. Please check your internet connection.';
const SCHEMA_ERROR = 'We received an unexpected response from the server.';

/**
 * Returns true for network-level fetch failures.
 * Different browsers produce different messages, so we match flexibly.
 */
function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;

  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch') || // Chrome/Edge
    msg.includes('networkerror') || // Firefox
    msg.includes('network request failed') ||
    msg.includes('load failed') // Safari
  );
}

/**
 * Translates a caught error into a user-friendly message for the ErrorBoundary.
 *
 * Only trusted error types have their messages shown to users:
 * - NavigableError: UI navigation errors with user-facing messages
 * - RetryableError: Retriable operation errors with user-facing messages
 * - ApiError: Messages from the BFF (already user-friendly)
 * - ZodError: Schema validation (fixed message)
 * - Network TypeError: Connectivity issues (fixed message)
 *
 * All other errors get a generic fallback to avoid leaking technical details.
 */
export function translateError(error: unknown): string {
  // NavigableError — user-facing navigation error
  if (error instanceof NavigableError) {
    return error.message;
  }

  // RetryableError — user-facing retriable error
  if (error instanceof RetryableError) {
    return error.message;
  }

  // ApiError — message comes from BFF, trusted
  if (error instanceof ApiError) {
    return error.message;
  }

  // ZodError — schema validation failure
  if (
    error instanceof ZodError ||
    (error instanceof Error && error.name === 'ZodError')
  ) {
    return SCHEMA_ERROR;
  }

  // Network error — browser couldn't reach the server
  if (isNetworkError(error)) {
    return NETWORK_ERROR;
  }

  // Everything else gets a generic fallback
  return FALLBACK;
}
